import { ArrowRight } from "@phosphor-icons/react";
import { C } from "@/brand/tokens";
import { PHOTOS } from "@/constants/photos";
import { positioning5 } from "@/brand/content";

/**
 * HeroSection5 — Deep Mineral Blue chamber.
 *
 * The ONLY dark chamber before consultation. Composed and editorial —
 * disciplined without the Roman-numeral chrome from Concept 4.
 *
 * Layout:
 *   - LEFT text rail (col 1-7): eyebrow, massive positioning headline,
 *     supporting line, dual CTA
 *   - RIGHT portrait (col 8-12): warm close-up in a Slate Olive matte
 *     frame with a small brass "two decades of practice" tab in the
 *     lower-right corner. No full bleed, no rotated marginalia — the
 *     hero reads as editorial letterhead, not a document spread.
 */
export function HeroSection5() {
  return (
    <section
      data-testid="hero"
      style={{
        background: C.surfacePrimary,
        color: C.textOnDark,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-14 items-center">
          {/* ── LEFT text rail ─────────────────────────────────────── */}
          <div className="lg:col-span-7">
            <div className="flex items-center gap-4 mb-9">
              <span
                aria-hidden="true"
                className="inline-block h-px w-14"
                style={{ background: C.accentMetal }}
              />
              <p
                className="font-inter text-[11px] uppercase font-semibold"
                style={{
                  color: C.accentMetal,
                  letterSpacing: "0.28em",
                }}
              >
                {positioning5.eyebrow}
              </p>
            </div>

            <h1
              data-testid="hero-headline"
              className="font-cormorant"
              style={{
                color: C.textOnDark,
                fontSize: "clamp(2.4rem, 5.6vw, 4.8rem)",
                fontWeight: 400,
                lineHeight: 1.02,
                letterSpacing: "-0.014em",
              }}
            >
              {positioning5.primary}
            </h1>

            <p
              data-testid="hero-subcopy"
              className="mt-10 max-w-[52ch] font-inter leading-[1.7]"
              style={{
                color: C.whiteOnDark,
                fontSize: "clamp(1.02rem, 1.15vw, 1.15rem)",
              }}
            >
              {positioning5.supporting}
            </p>

            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-4">
              <a
                href="#contact"
                data-testid="hero-primary-cta"
                className="group inline-flex items-center gap-3 px-7 py-4 font-inter text-[12px] uppercase font-semibold transition-colors"
                style={{
                  background: C.accentMetal,
                  color: C.bgDark,
                  letterSpacing: "0.22em",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = C.textOnDark)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = C.accentMetal)
                }
              >
                {positioning5.primaryCta}
                <ArrowRight
                  size={14}
                  weight="bold"
                  className="group-hover:translate-x-1 transition-transform"
                />
              </a>
              <a
                href="#pathways"
                data-testid="hero-secondary-cta"
                className="group inline-flex items-center gap-2 pb-1.5 font-inter text-[11.5px] uppercase font-semibold border-b transition-colors"
                style={{
                  color: C.textOnDark,
                  borderColor: C.brassLine,
                  letterSpacing: "0.22em",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = C.accentMetal;
                  e.currentTarget.style.borderColor = C.accentMetal;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = C.textOnDark;
                  e.currentTarget.style.borderColor = C.brassLine;
                }}
              >
                {positioning5.secondaryCta}
                <ArrowRight
                  size={12}
                  weight="bold"
                  className="group-hover:translate-x-1 transition-transform"
                />
              </a>
            </div>
          </div>

          {/* ── RIGHT portrait ─────────────────────────────────────── */}
          <div className="lg:col-span-5">
            <figure className="relative">
              {/* Slate Olive matte frame — deliberate architectural border */}
              <div
                className="relative p-3"
                style={{
                  background: C.supportOrganic,
                  border: `1px solid ${C.brassSoft}`,
                }}
              >
                <div
                  className="relative overflow-hidden"
                  style={{
                    aspectRatio: "4 / 5",
                    background: C.bgDark,
                  }}
                >
                  <img
                    src={PHOTOS.warmCloseUp}
                    alt="Katrina Bernard, Founder & Principal of Faith 1st"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{
                      objectPosition: "center 22%",
                      filter: "saturate(0.85) contrast(1.03)",
                    }}
                    loading="eager"
                  />
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background: `linear-gradient(180deg, rgba(22,24,27,0) 60%, rgba(22,24,27,0.35) 100%)`,
                    }}
                  />
                </div>
              </div>

              {/* Founder caption — quiet, editorial */}
              <figcaption className="mt-5 flex items-start justify-between gap-4">
                <div>
                  <p
                    className="font-cormorant"
                    style={{
                      color: C.textOnDark,
                      fontSize: "1.1rem",
                      lineHeight: 1.25,
                    }}
                  >
                    Katrina Bernard, JD&nbsp;|&nbsp;CPC
                  </p>
                  <p
                    className="mt-1.5 font-inter text-[10px] uppercase font-semibold"
                    style={{
                      color: C.accentMetal,
                      letterSpacing: "0.28em",
                    }}
                  >
                    Founder &amp; Principal
                  </p>
                </div>
                <div className="text-right">
                  <p
                    className="font-inter text-[9.5px] uppercase font-bold"
                    style={{
                      color: C.accentMetal,
                      letterSpacing: "0.28em",
                    }}
                  >
                    Two decades
                  </p>
                  <p
                    className="mt-1 font-cormorant italic"
                    style={{
                      color: C.whiteOnDark,
                      fontSize: "0.9rem",
                    }}
                  >
                    of practice
                  </p>
                </div>
              </figcaption>
            </figure>
          </div>
        </div>
      </div>
    </section>
  );
}
